import React from 'react';
import { Document } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { Stack, Typography, SlideProps, IconButton, Fade } from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardIos';
import BasicLoader from '../Loaders/BasicLoader';
import PdfViewerBody from './PdfViewerBody';

import { ISession } from '../../store/SessionSlice';
import { TP_COLORS } from '../../constants';
import { isMobileScreen } from '../../helper';

interface PdfViewerProps {
  fileUrl: string;
  onLoadSuccess: (numPages: number) => void;
  selectedPageNumber: string;
  pageWidth?: number;
  scale: number;
  session: ISession;
  onChangePage: (pageNo: string) => void;
  isWhiteboardEnable: boolean;
}

interface IState {
  totalPages: number;
  animationDirection: SlideProps['direction'];
}

const CustomLoaderComponent = (
  <div>
    <BasicLoader />
  </div>
);
const CustomErrorComponent = (
  <Typography
    variant="h5"
    fontWeight={600}
    textAlign="center"
    sx={{ m: '30px 0' }}
  >
    Failed to load PDF file
  </Typography>
);

export default class PdfViewer extends React.Component<PdfViewerProps, IState> {
  constructor(props: PdfViewerProps) {
    super(props);
    this.state = {
      totalPages: 0,
      animationDirection: undefined,
    };
  }

  // LIFECYCLE METHODS
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  shouldComponentUpdate(
    nextProps: Readonly<PdfViewerProps>,
    nextState: Readonly<IState>
  ) {
    const {
      selectedPageNumber,
      pageWidth,
      scale,
      fileUrl,
      isWhiteboardEnable,
    } = this.props;
    const { totalPages, animationDirection } = this.state;

    if (
      selectedPageNumber !== nextProps.selectedPageNumber ||
      scale !== nextProps.scale
    ) {
      // eslint-disable-next-line no-undef-init
      let _dir: SlideProps['direction'] = undefined;
      this.setState((prevState) => {
        if (nextProps.selectedPageNumber > selectedPageNumber) {
          _dir = 'left';
        } else if (nextProps.selectedPageNumber < selectedPageNumber) {
          _dir = 'right';
        }
        return {
          ...prevState,
          animationDirection: _dir,
        };
      });
    }

    return (
      selectedPageNumber !== nextProps.selectedPageNumber ||
      totalPages !== nextState.totalPages ||
      pageWidth !== nextProps.pageWidth ||
      animationDirection !== nextState.animationDirection ||
      scale !== nextProps.scale ||
      fileUrl !== nextProps.fileUrl ||
      isWhiteboardEnable !== nextProps.isWhiteboardEnable
    );
  }

  // RENDER
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  render() {
    const { totalPages } = this.state;
    const {
      fileUrl,
      onLoadSuccess,
      selectedPageNumber,
      pageWidth,
      scale,
      session,
      onChangePage,
      isWhiteboardEnable,
    } = this.props;

    const questions = session?.questions;

    const isBackPageButtonDisabled = (Number(selectedPageNumber) || 1) < 2;
    const nextNextPageButtonDisabled =
      (Number(selectedPageNumber) || 1) >= totalPages;

    const isMobile = isMobileScreen();

    return (
      <div
        style={{
          minHeight: '500px',
          visibility: isWhiteboardEnable ? 'hidden' : 'visible',
        }}
      >
        <Document
          renderMode="canvas"
          file={{ url: fileUrl }}
          loading={CustomLoaderComponent}
          error={CustomErrorComponent}
          onLoadSuccess={({ numPages }) => {
            onLoadSuccess(numPages);
            this.setState((prevState) => ({
              ...prevState,
              totalPages: numPages + questions.length,
            }));
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-evenly"
            alignItems="center"
          >
            {!isMobile && (
              <IconButton
                color="primary"
                sx={{
                  backgroundColor: TP_COLORS.white,
                  boxShadow: 1,
                  padding: '10px',
                  marginRight: {
                    xs: '5px',
                    sm: '10px',
                    md: '20px',
                    lg: '50px',
                  },
                }}
                onClick={() =>
                  onChangePage((Number(selectedPageNumber) - 1).toString())
                }
                disabled={isBackPageButtonDisabled}
              >
                <ArrowBackIcon />
              </IconButton>
            )}

            <Fade in timeout={600}>
              <Stack alignItems="center">
                <PdfViewerBody
                  {...{
                    selectedPageNumber,
                    pageWidth,
                    scale,
                  }}
                />
              </Stack>
            </Fade>

            {!isMobile && (
              <IconButton
                color="primary"
                sx={{
                  backgroundColor: TP_COLORS.white,
                  boxShadow: 1,
                  padding: '10px',
                  marginLeft: {
                    xs: '5px',
                    sm: '10px',
                    md: '20px',
                    lg: '50px',
                  },
                }}
                onClick={() =>
                  onChangePage((Number(selectedPageNumber) + 1).toString())
                }
                disabled={nextNextPageButtonDisabled}
              >
                <ArrowForwardIcon />
              </IconButton>
            )}
          </Stack>
        </Document>
      </div>
    );
  }
}
